// Slice 14 — Billing & Usage page.
// Per-seat plan selection + metered overage. Stripe Checkout via emergentintegrations.
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CheckCircle2, Loader2, Sparkles, Zap, ShieldCheck, CreditCard,
  Phone, Brain, MessageSquare, AlertTriangle, RefreshCw, KeyRound,
  RotateCw, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const PLAN_ICONS = { starter: Zap, pro: Sparkles, enterprise: ShieldCheck };

export default function BillingPage() {
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [overview, setOverview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(null); // {plan_id, label, price}
  const [kmsState, setKmsState] = useState(null);
  const [kmsBusy, setKmsBusy] = useState(false);
  const [rotateResult, setRotateResult] = useState(null);
  const { canWrite } = useCurrentModulePerms();

  async function load() {
    setBusy(true);
    try {
      const [p, s, k] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/subscription"),
        api.get("/admin/kms/status").catch(() => ({ data: null })),
      ]);
      setPlans(p.data.plans || []);
      setOverview(s.data);
      setKmsState(k.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }
  useEffect(() => { load(); }, []);

  // Returning from Stripe — poll status for up to 30s
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let cancelled = false;
    let attempts = 0;
    async function poll() {
      while (!cancelled && attempts < 10) {
        attempts += 1;
        try {
          const r = await api.get(`/billing/checkout/status/${sid}`);
          if (r.data.payment_status === "paid") {
            toast.success(`Payment successful · $${r.data.amount_total / 100}`);
            await load();
            setParams({}, { replace: true });
            return;
          }
          if (r.data.status === "expired") {
            toast.error("Checkout session expired");
            setParams({}, { replace: true });
            return;
          }
        } catch (e) { /* keep polling */ }
        await new Promise((res) => setTimeout(res, 2000));
      }
      if (!cancelled) {
        toast.message("Still processing — refresh later to check status");
        setParams({}, { replace: true });
      }
    }
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [params.get("session_id")]);

  useEffect(() => {
    if (params.get("cancelled")) {
      toast.message("Checkout cancelled");
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  async function rotateKms() {
    if (!window.confirm("Rotate the vault encryption key? All encrypted secrets will be re-encrypted with a NEW key. You'll need to copy that new key into backend/.env and restart workers. Continue?")) return;
    setKmsBusy(true);
    try {
      const r = await api.post("/admin/kms/rotate");
      setRotateResult(r.data);
      toast.success(`Rotated · ${r.data.re_encrypted_total} ciphertexts re-encrypted`);
      const k = await api.get("/admin/kms/status");
      setKmsState(k.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setKmsBusy(false); }
  }

  const sub = overview?.subscription;
  const usage = overview?.usage || {};
  const breakdown = overview?.overage?.items || [];

  return (
    <div className="px-4 sm:px-6 py-6 max-w-6xl" data-testid="billing-page">
      <div className="mb-5 flex justify-between items-start gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Billing & Usage</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Per-seat monthly subscription + metered overage. Powered by Stripe (test mode).
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={busy} className="gap-1.5" data-testid="billing-refresh">
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>} Refresh
        </Button>
      </div>

      {/* Current subscription card */}
      {sub && overview && (
        <Card className="p-5 mb-6 border border-primary/30 bg-primary/5" data-testid="billing-current">
          <div className="flex flex-wrap gap-4 items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Current plan</div>
              <div className="flex items-center gap-2 mt-1">
                <h2 className="font-display text-xl font-bold">{overview.plan.label}</h2>
                <Badge variant="outline" className="text-[10px]">{sub.status}</Badge>
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {sub.seats} seat{sub.seats === 1 ? "" : "s"} ×
                ${overview.plan.price_per_seat_usd}/seat/mo =
                <span className="font-semibold text-foreground"> ${overview.seat_amount_usd}/mo</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Period: {sub.current_period_start.slice(0, 10)} → {sub.current_period_end.slice(0, 10)}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Projected this month</div>
              <div className="text-2xl font-display font-bold mt-1" data-testid="projected-total">
                ${overview.projected_total_usd}
              </div>
              {overview.overage.total_usd > 0 && (
                <div className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                  Includes ${overview.overage.total_usd} overage
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Usage tiles */}
      {sub && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="usage-tiles">
          <UsageTile
            label="Voice minutes"
            icon={Phone}
            used={usage.voice_minutes || 0}
            included={(overview.plan.included_per_seat.voice_minutes * sub.seats)}
            unit="min"
            tone="blue"
            overage={breakdown.find((b) => b.kind === "voice_minutes")}
            testid="usage-tile-voice"
          />
          <UsageTile
            label="AI tokens"
            icon={Brain}
            used={usage.ai_tokens || 0}
            included={(overview.plan.included_per_seat.ai_tokens * sub.seats)}
            unit="tokens"
            tone="violet"
            overage={breakdown.find((b) => b.kind === "ai_tokens")}
            testid="usage-tile-ai"
          />
          <UsageTile
            label="SMS"
            icon={MessageSquare}
            used={usage.sms || 0}
            included={(overview.plan.included_per_seat.sms * sub.seats)}
            unit="msgs"
            tone="emerald"
            overage={breakdown.find((b) => b.kind === "sms")}
            testid="usage-tile-sms"
          />
        </div>
      )}

      {/* Plan picker */}
      <h2 className="font-display text-lg font-bold mb-3 mt-8">Choose a plan</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="plan-grid">
        {plans.map((p) => {
          const Icon = PLAN_ICONS[p.id] || Sparkles;
          const isCurrent = sub?.plan_id === p.id;
          return (
            <Card key={p.id}
                  className={`p-4 flex flex-col gap-2 border-2 transition-all
                    ${p.is_popular ? "border-primary shadow-lg" : "border-border"}
                    ${isCurrent ? "ring-2 ring-emerald-400" : ""}`}
                  data-testid={`plan-card-${p.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className="w-5 h-5 text-primary"/>
                  <h3 className="font-display font-bold">{p.label}</h3>
                </div>
                {p.is_popular && <Badge className="text-[10px] bg-primary text-primary-foreground">Popular</Badge>}
                {isCurrent && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Current</Badge>}
              </div>
              <p className="text-xs text-muted-foreground">{p.tagline}</p>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="text-3xl font-display font-bold">${p.price_per_seat_usd}</span>
                <span className="text-xs text-muted-foreground">/ seat / month</span>
              </div>
              <ul className="text-xs space-y-1 mt-2">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5"/>{f}</li>
                ))}
              </ul>
              <div className="text-[11px] text-muted-foreground mt-2 pt-2 border-t border-border">
                Includes per seat / month:
                <div className="mt-1 flex flex-wrap gap-1">
                  <Badge variant="outline" className="text-[10px]">{p.included_per_seat.voice_minutes} min</Badge>
                  <Badge variant="outline" className="text-[10px]">{(p.included_per_seat.ai_tokens / 1000).toFixed(0)}k tokens</Badge>
                  <Badge variant="outline" className="text-[10px]">{p.included_per_seat.sms} SMS</Badge>
                </div>
                <div className="mt-2">
                  Overage: ${p.overage_rates_usd.voice_minutes}/min · ${p.overage_rates_usd.ai_tokens_per_1k}/1k tok · ${p.overage_rates_usd.sms}/SMS
                </div>
              </div>
              <Button
                onClick={() => setPicker(p)}
                className={`mt-auto ${isCurrent ? "" : "bg-primary hover:bg-primary/90 text-primary-foreground"}`}
                variant={isCurrent ? "outline" : "default"}
                data-testid={`plan-select-${p.id}`}>
                {isCurrent ? "Change seats" : "Choose plan"}
              </Button>
            </Card>
          );
        })}
      </div>

      {/* Overage charge */}
      {overview?.overage?.total_usd > 0 && (
        <Card className="p-4 mb-6 border border-amber-300 bg-amber-50 dark:bg-amber-500/10" data-testid="overage-card">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-1"/>
            <div className="flex-1">
              <h3 className="font-display font-bold text-amber-900 dark:text-amber-200">Overage charges accrued</h3>
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                Your tenant has exceeded the plan's included quota. Pay overage to keep service uninterrupted.
              </p>
              <div className="mt-2 space-y-0.5 text-xs">
                {breakdown.filter((b) => b.overage_cost_usd > 0).map((b) => (
                  <div key={b.kind}>
                    <strong>{b.overage_qty.toFixed(1)}</strong> {b.kind.replace("_", " ")} over → ${b.overage_cost_usd.toFixed(2)}
                  </div>
                ))}
              </div>
            </div>
            <Button onClick={() => payOverage()} className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5" data-testid="pay-overage">
              <CreditCard className="w-4 h-4"/> Pay ${overview.overage.total_usd}
            </Button>
          </div>
        </Card>
      )}

      {/* KMS Rotate card */}
      <h2 className="font-display text-lg font-bold mb-3 mt-8 flex items-center gap-2">
        <KeyRound className="w-5 h-5"/> Vault key (KMS)
      </h2>
      <Card className="p-4" data-testid="kms-card">
        {kmsState && (
          <>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <Label className="text-xs">Active fingerprint</Label>
                <div className="font-mono text-xs mt-1">{kmsState.active_fingerprint || "—"}</div>
              </div>
              <div>
                <Label className="text-xs">Encrypted secrets in vault</Label>
                <div className="text-xs mt-1">{kmsState.total_ciphertexts} ciphertexts across {Object.keys(kmsState.ciphertext_counts || {}).length} collections</div>
              </div>
            </div>
            {canWrite && <Button onClick={rotateKms} disabled={kmsBusy} variant="outline" className="mt-3 gap-1.5" data-testid="kms-rotate-btn">
              {kmsBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <RotateCw className="w-4 h-4"/>} Rotate vault key
            </Button>}
            <p className="text-xs text-muted-foreground mt-2">
              Generates a new Fernet key, re-encrypts all stored secrets, and surfaces the new key once.
              You must persist the new key in <code>backend/.env</code> as <code>CONN7_VAULT_KEY</code> and restart workers.
            </p>
          </>
        )}
      </Card>

      {/* Seat picker dialog */}
      {picker && <SeatPickerDialog plan={picker} currentSeats={sub?.seats || 1}
                                    onClose={() => setPicker(null)}/>}

      {/* New-key reveal */}
      <Dialog open={!!rotateResult} onOpenChange={(o) => !o && setRotateResult(null)}>
        <DialogContent data-testid="kms-rotate-dialog">
          <DialogHeader><DialogTitle>New vault key — shown once</DialogTitle></DialogHeader>
          {rotateResult && (
            <div className="space-y-3">
              <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 rounded-md p-2 border border-amber-200">
                <strong>Persist this immediately</strong> in <code>backend/.env</code> as
                <code> CONN7_VAULT_KEY=...</code>, then restart workers. Otherwise newly-encrypted
                secrets won't survive a process restart.
              </div>
              <div>
                <Label>New CONN7_VAULT_KEY</Label>
                <Input readOnly value={rotateResult.new_key_once || ""} className="font-mono text-xs" data-testid="kms-new-key"/>
              </div>
              <div className="text-xs">
                Fingerprint: <code>{rotateResult.fingerprint}</code> · Version {rotateResult.version} ·
                Re-encrypted {rotateResult.re_encrypted_total} secret(s){rotateResult.skipped.length > 0 ? `; skipped ${rotateResult.skipped.length}` : ""}.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setRotateResult(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  async function payOverage() {
    setBusy(true);
    try {
      const r = await api.post("/billing/checkout/overage", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(formatErr(e)); setBusy(false); }
  }
}

function UsageTile({ label, icon: Icon, used, included, unit, tone, overage, testid }) {
  const pct = included > 0 ? Math.min(100, (used / included) * 100) : 0;
  const isOver = used > included && included > 0;
  const tones = {
    blue:    { bar: "bg-blue-500", text: "text-blue-700 dark:text-blue-300", bg: "bg-blue-50 dark:bg-blue-500/10" },
    violet:  { bar: "bg-violet-500", text: "text-violet-700 dark:text-violet-300", bg: "bg-violet-50 dark:bg-violet-500/10" },
    emerald: { bar: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-300", bg: "bg-emerald-50 dark:bg-emerald-500/10" },
  };
  const t = tones[tone] || tones.blue;
  const overBar = isOver ? "bg-amber-500" : t.bar;
  return (
    <Card className={`p-4 ${t.bg}`} data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${t.text}`}/>
        <span className={`text-[11px] uppercase tracking-wider ${t.text}`}>{label}</span>
      </div>
      <div className="font-display font-bold text-2xl mt-2">{Math.round(used).toLocaleString()}<span className="text-xs font-normal text-muted-foreground"> / {Math.round(included).toLocaleString()} {unit}</span></div>
      <div className="h-1.5 rounded-full bg-muted mt-2 overflow-hidden">
        <div className={`h-full ${overBar} transition-all`} style={{ width: `${pct}%` }}/>
      </div>
      {overage && overage.overage_cost_usd > 0 && (
        <div className="text-xs text-amber-700 dark:text-amber-300 mt-1">
          +${overage.overage_cost_usd.toFixed(2)} overage
        </div>
      )}
    </Card>
  );
}

function SeatPickerDialog({ plan, currentSeats, onClose }) {
  const [seats, setSeats] = useState(currentSeats || 1);
  const [busy, setBusy] = useState(false);
  const amount = (plan.price_per_seat_usd * Math.max(1, parseInt(seats || 1))).toFixed(2);
  async function checkout() {
    setBusy(true);
    try {
      const r = await api.post("/billing/checkout/seat", {
        plan_id: plan.id,
        seats: parseInt(seats || 1),
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) { toast.error(formatErr(e)); setBusy(false); }
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="seat-picker-dialog">
        <DialogHeader><DialogTitle>{plan.label} · choose seats</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Number of seats</Label>
            <Input type="number" min={1} max={500} value={seats}
              onChange={(e) => setSeats(e.target.value)} data-testid="seat-count"/>
          </div>
          <div className="text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">{seats} × ${plan.price_per_seat_usd}/seat</span><span className="font-semibold">${amount}</span></div>
            <div className="text-xs text-muted-foreground mt-1">
              Includes {plan.included_per_seat.voice_minutes * seats} voice min, {(plan.included_per_seat.ai_tokens * seats / 1000).toFixed(0)}k AI tokens, {plan.included_per_seat.sms * seats} SMS / month.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={checkout} disabled={busy || !seats || seats < 1}
            className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"
            data-testid="seat-checkout">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <ExternalLink className="w-4 h-4"/>} Pay ${amount}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
